<template>
  <div class="topbar-item">
    <!-- Simple Icon Button to Toggle QuickUser Panel -->
    <div
      v-show="!hideToggle"
      class="btn btn-icon btn-clean btn-lg mr-1 quick-user-toggle smallIcon"
      id="kt_quick_user_toggle"
      @click="toggleQuickUser"
    >
      <i class="mdi mdi-account responsive-text"></i>
    </div>

    <!-- QuickUser Offcanvas Panel -->
    <div id="kt_quick_user" ref="kt_quick_user" class="offcanvas offcanvas-right p-10">
      <!--begin::Header-->
      <div class="offcanvas-header d-flex align-items-center justify-content-between pb-5">
        <h3 class="font-weight-bold m-0">
          User Profile
        </h3>
        <a href="#" class="btn btn-xs btn-icon btn-light btn-hover-primary" id="kt_quick_user_close"
          @click="closeQuickUser">
          <i class="ki ki-close icon-xs text-muted"></i>
        </a>
      </div>
      <!--end::Header-->

      <!--begin::Content-->
      <perfect-scrollbar class="offcanvas-content pr-5 mr-n5 scroll"
        style="max-height: calc(100vh - 120px); position: relative;">
        <div class="d-flex align-items-center mt-5">
          <div class="d-flex flex-column">
            <router-link to="/custom-pages/profile"
              class="font-weight-bold font-size-h5 text-dark-75 user-profile-link">
              {{ displayName }}
            </router-link>
            <div class="navi mt-2">
              <a href="#" class="navi-item">
                <span class="navi-link p-0 pb-2">
                  <span class="navi-text text-muted email-link">
                    {{ displayEmail }}
                  </span>
                </span>
              </a>
            </div>
            <button v-if="isImpersonating" class="btn btn-light-danger btn-bold" @click="endImpersonation">
              Switch Back to Admin
            </button>
            <button v-else class="btn sign-out-btn btn-bold" @click="onLogout">
              Sign out
            </button>
          </div>
        </div>
        <div class="separator separator-dashed mt-8 mb-5"></div>
      </perfect-scrollbar>
      <!--end::Content-->
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { authGet } from "@/core/services/auth-storage";
import { LOGOUT, END_IMPERSONATION } from "@/core/services/store/auth.module";
import KTLayoutQuickUser from "@/assets/js/layout/extended/quick-user.js";
import KTOffcanvas from "@/assets/js/components/offcanvas.js";

export default {
  name: "KTQuickUser",
  props: {
    hideToggle: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isStaff: false,
      staffInfo: null
    };
  },
  computed: {
    ...mapGetters([
      "currentUserPersonalInfo",
      "isImpersonating"
    ]),
    displayName() {
      // If impersonating, show the impersonated user's name
      if (this.isImpersonating) {
        return this.currentUserPersonalInfo.name;
      }
      // If staff, show staff name
      if (this.isStaff && this.staffInfo) {
        return this.staffInfo.name;
      }
      // Otherwise show regular user name
      return this.currentUserPersonalInfo.name;
    },
    displayEmail() {
      // If impersonating, show the impersonated user's email
      if (this.isImpersonating) {
        return this.currentUserPersonalInfo.email;
      }
      // If staff, show staff email
      if (this.isStaff && this.staffInfo) {
        return this.staffInfo.email;
      }
      // Otherwise show regular user email
      return this.currentUserPersonalInfo.email;
    }
  },
  methods: {
    onLogout() {
      this.$store
        .dispatch(LOGOUT)
        .then(() => this.$router.push({ name: "login" }));
    },
    endImpersonation() {
      this.$store
        .dispatch(END_IMPERSONATION)
        .then(() => {
          this.closeQuickUser();
          this.$router.push({ name: "businesses" });
        })
        .catch(() => {
          this.$router.push({ name: "login" });
        });
    },
    checkStaffStatus() {
      // Check session storage for staff status
      const isStaff = authGet('is_staff');
      this.isStaff = isStaff === '1';
      if (this.isStaff) {
        try {
          const staffData = sessionStorage.getItem('staff');
          if (staffData) {
            this.staffInfo = JSON.parse(staffData);
          }
        } catch (e) {
          console.error('Error parsing staff data:', e);
        }
      }
    },
    toggleQuickUser() {
      const offcanvasElement = this.$refs["kt_quick_user"];
      if (offcanvasElement) {
        if (offcanvasElement.classList.contains('offcanvas-on')) {
          this.closeQuickUser();
        } else {
          this.openQuickUser();
        }
      }
    },
    openQuickUser() {
      const offcanvasElement = this.$refs["kt_quick_user"];
      if (offcanvasElement) {
        offcanvasElement.classList.add('offcanvas-on');
        offcanvasElement.style.transform = 'translateX(0)';
        offcanvasElement.style.visibility = 'visible';
        offcanvasElement.style.pointerEvents = 'auto';
      }
    },
    closeQuickUser() {
      const offcanvasElement = this.$refs["kt_quick_user"];
      if (offcanvasElement) {
        offcanvasElement.classList.remove('offcanvas-on');
        offcanvasElement.style.transform = 'translateX(100%)';
        offcanvasElement.style.visibility = 'hidden';
        offcanvasElement.style.pointerEvents = 'none';
        offcanvasElement.style.removeProperty('display');
        offcanvasElement.style.removeProperty('z-index');
      }
      document.body.classList.remove('quick-user-open');
      const overlay = document.querySelector('.quick-user-overlay');
      if (overlay) {
        overlay.remove();
      }
    }
  },
  mounted() {
    this.checkStaffStatus();

    // Initialize the QuickUser panel
    this.$nextTick(() => {
      try {
        // Init Quick User Panel
        KTLayoutQuickUser.init(this.$refs["kt_quick_user"]);
      } catch (error) {
        console.error('Error initializing QuickUser:', error);
      }
    });
  }
};
</script>

<style lang="scss" scoped>
/* QuickUser Toggle Button */
.quick-user-toggle {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-radius: 8px !important;
  padding: 8px !important;
  transition: all 0.3s ease !important;
  backdrop-filter: blur(10px) !important;

  &:hover {
    background: rgba(255, 255, 255, 0.2) !important;
    transform: scale(1.05) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  }

  &:active {
    transform: scale(0.95) !important;
  }

  i {
    font-size: 18px !important;
  }
}

/* Responsive Text Class */
.responsive-text {
  color: white !important;
}

/* QuickUser Offcanvas Panel */
#kt_quick_user {
  position: fixed !important;
  top: 0 !important;
  right: 0 !important;
  height: 100vh !important;
  width: 375px !important;
  z-index: 9999 !important;
  background: white !important;
  box-shadow: 0px 1px 9px -3px rgba(0, 0, 0, 0.25) !important;
  transform: translateX(100%) !important;
  visibility: hidden !important;
  pointer-events: none !important;
  transition: transform 0.3s ease, visibility 0.3s ease !important;

  &.offcanvas-on {
    transform: translateX(0) !important;
    visibility: visible !important;
    pointer-events: auto !important;
  }

  .offcanvas-content {
    height: calc(100vh - 80px);
    overflow-y: auto;
    padding-right: 1rem;
  }

  .offcanvas-header {
    position: sticky;
    top: 0;
    background: white;
    z-index: 10;
    border-bottom: 1px solid #e4e6ea;
    padding: 1.5rem 2rem 1rem 2rem;
  }
}

/* User Profile Link */
.user-profile-link {
  color: #181C32 !important;
  text-decoration: none !important;
  transition: color 0.3s ease !important;
  background: transparent !important;

  &:hover {
    color: #241B5A !important;
    background: transparent !important;
    text-decoration: none !important;
    box-shadow: none !important;
  }
}

/* Email Link */
.email-link {
  color: #6C7293 !important;
  transition: color 0.3s ease !important;
  background: transparent !important;

  &:hover {
    color: #241B5A !important;
    background: transparent !important;
    box-shadow: none !important;
  }
}

/* Sign Out Button */
.sign-out-btn {
  background: #241B5A !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 0.75rem 1.5rem !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 6px rgba(36, 27, 90, 0.25) !important;

  &:hover {
    background: #16124a !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 6px 12px rgba(36, 27, 90, 0.3) !important;
  }

  &:active {
    transform: translateY(0) !important;
  }
}

/* Close Button */
#kt_quick_user_close {
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  border-radius: 4px !important;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05) !important;
    transform: scale(1.1) !important;
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.1) !important;
    transform: scale(0.95) !important;
  }
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .quick-user-toggle {
    padding: 8px !important;

    i {
      font-size: 18px !important;
    }
  }

  #kt_quick_user {
    width: 100% !important;
    max-width: 375px !important;
  }
}

@media (max-width: 576px) {
  .quick-user-toggle {
    padding: 6px !important;

    i {
      font-size: 16px !important;
    }
  }

  #kt_quick_user {
    width: 100% !important;
    max-width: 100vw !important;
  }

  /* Responsive text behavior for small screens */
  .responsive-text {
    color: var(--primary) !important;
  }

  .smallIcon i {
    font-size: 33px !important;
    padding-top: 2px !important;
  }
}
</style>